import { createFileRoute } from "@tanstack/react-router";
import { Button, LoginCard, TextField } from "@op/sf-editor";
import { useAuth } from "../../hooks/useAuth";
import { Navigate } from "@tanstack/react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import z from "zod";
import { env } from "../../utils/env";
import { authRouterClient } from "../../lib/authRouterClient";
import AltchaWorker from "altcha-lib/worker?worker";
import { solveChallengeWorkers } from "altcha-lib";
import { toast } from "sonner";

export const Route = createFileRoute("/login/")({
  component: LoginRoute,
});

const { fieldContext, formContext } = createFormHookContexts();

const { useAppForm } = createFormHook({
  fieldComponents: {
    TextField: TextField.Input,
  },
  formComponents: {
    SubmitButton: Button,
  },
  fieldContext,
  formContext,
});

function LoginRoute() {
  const session = useAuth();
  const { t } = useLingui();

  const { handleSubmit, AppField, AppForm, SubmitButton, Subscribe } =
    useAppForm({
      defaultValues: {
        email: "",
      },
      validators: {
        // Pass a schema or function to validate
        onChangeAsync: z.object({
          email: z.email({ message: t`Please enter a valid email address` }),
        }),
        onChangeAsyncDebounceMs: 500,
      },
      onSubmit: async ({ value }) => {
        const challengeRes = await authRouterClient.custom.challenge.$get();
        const { challenge, salt, signature, maxnumber, algorithm } =
          await challengeRes.json();

        const solution = await solveChallengeWorkers(
          () => new AltchaWorker(),
          8,
          challenge,
          salt,
          algorithm,
          maxnumber,
          0,
        );
        if (!solution) throw new Error("Captcha solution not found");

        const res = await authRouterClient.custom["sign-in"][
          "magic-link"
        ].$post({
          json: {
            email: value.email,
            callbackURL: new URL("/app", env.VITE_PUBLIC_URL).toString(),
            captcha: {
              challenge,
              solution: solution.number,
              salt,
              signature,
            },
          },
        });
        if (!res.ok) {
          toast.error(t`Failed to send login link`, {
            description: t`Please try again later`,
          });
          return;
        }
        toast.success(t`Login link sent`, {
          description: t`Check your email for a login link`,
        });
      },
    });

  if (session.data !== null) {
    return <Navigate to="/app" />;
  }
  return (
    <div className="flex h-full min-h-screen w-full flex-wrap items-center justify-center gap-12 bg-default-background px-12 py-12">
      {/* <div className="flex max-w-[576px] grow shrink-0 basis-0 flex-col items-center justify-center gap-12 self-stretch">
        <Benefits>
          <Benefits.Benefit
            icon={<FeatherLightbulb />}
            title={<Trans>Spark your imagination</Trans>}
            description={
              <Trans>
                Dive into a world where your creative ideas are instantly
                brought to life. Let’s paint your thoughts in digital strokes.
              </Trans>
            }
          />
          <Benefits.Benefit
            icon={<FeatherRocket />}
            title={<Trans>Simplify the complex</Trans>}
            description={
              <Trans>
                Say goodbye to mundane tasks. Our AI streamlines your workflow,
                freeing you to focus on what truly matters.
              </Trans>
            }
          />
          <Benefits.Benefit
            icon={<FeatherZap />}
            title={<Trans>Boost your brainpower</Trans>}
            description={
              <Trans>
                Elevate your learning with tailored insights and resources. It's
                like having a personal coach in your pocket.
              </Trans>
            }
          />
        </Benefits>
      </div> */}
      <LoginCard
        title={
          <img
            src="/images/openpaper-logo.svg"
            alt="OpenPaper Logo"
            className="h-5"
          />
        }
      >
        <form
          className="contents"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(e);
          }}
        >
          <div className="flex w-full flex-col items-start justify-center gap-6">
            <AppField
              name="email"
              children={(field) => (
                <TextField
                  className="h-auto w-full flex-none"
                  label={<Trans>Your Email</Trans>}
                  error={!field.state.meta.isValid}
                  helpText={
                    !field.state.meta.isValid && (
                      <span className="text-error-500">
                        {field.state.meta.errors
                          .map((e) => e?.message)
                          .join(", ")}
                      </span>
                    )
                  }
                >
                  <field.TextField
                    placeholder={t`Enter your email`}
                    value={field.state.value}
                    name={field.name}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </TextField>
              )}
            />
          </div>
          <AppForm>
            <Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <SubmitButton
                  className="h-10 w-full flex-none"
                  variant="brand-secondary"
                  size="large"
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  loading={isSubmitting}
                >
                  <Trans>Send Login Link</Trans>
                </SubmitButton>
              )}
            />
          </AppForm>
        </form>
      </LoginCard>
    </div>
  );
}
